const ANIMATION_DURATION_MS = 250;

/**
 * The open/close of a page's management panel (`List settings`, `Manage ranking`): the one owner of
 * its `open`, `is-animated` / `is-expanded` and the trigger's `aria-expanded`.
 *
 * A native `<details>` shows or drops its content at once, so the trigger takes the toggle over: it
 * opens first and lets the CSS grow the row, and on a close it waits for the CSS to collapse the row
 * before `open` goes false. Without JS the panel is a plain `<details>`.
 */
export function initDisclosurePanels(documentRef = globalThis.document, windowRef = globalThis.window) {
    const panels = Array.from(documentRef?.querySelectorAll?.('[data-ui-disclosure]') ?? []).filter((panel) => {
        return panel instanceof HTMLDetailsElement;
    });

    panels.forEach((panel) => {
        if (panel.dataset.uiDisclosureBound === 'true') {
            return;
        }

        const trigger = panel.querySelector('.ui-disclosure-trigger');
        if (!(trigger instanceof HTMLElement)) {
            return;
        }

        panel.dataset.uiDisclosureBound = 'true';
        panel.classList.add('is-animated');

        let isAnimating = false;
        // Read per click: with reduced motion the CSS drops the transition, so nothing is worth waiting for.
        const prefersReducedMotion = () => windowRef?.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

        const syncExpandedState = (expanded) => {
            panel.classList.toggle('is-expanded', expanded);
            trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        };

        syncExpandedState(panel.open);

        trigger.addEventListener('click', (event) => {
            event.preventDefault();

            if (isAnimating) {
                return;
            }

            const shouldExpand = !panel.classList.contains('is-expanded');

            if (prefersReducedMotion()) {
                panel.open = shouldExpand;
                syncExpandedState(shouldExpand);

                return;
            }

            isAnimating = true;

            if (shouldExpand) {
                panel.open = true;
                windowRef.requestAnimationFrame(() => {
                    syncExpandedState(true);
                    windowRef.setTimeout(() => {
                        isAnimating = false;
                    }, ANIMATION_DURATION_MS);
                });

                return;
            }

            syncExpandedState(false);
            windowRef.setTimeout(() => {
                panel.open = false;
                isAnimating = false;
            }, ANIMATION_DURATION_MS);
        });
    });
}
